import { message, Spin } from 'antd'
import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import {
    apiGetTagList,
    getEpicsListForTC,
    getSprintListForTC,
} from '../redux/testRun/testRun.service'
import { getCurrentProjectId } from './helpers'

function resolveProjectId(projectId) {
    return projectId || getCurrentProjectId()
}

export function createFetchSprints(projectId) {
    return (params) => fetchSprints({ ...params, projectId: resolveProjectId(projectId) })
}

export function createFetchEpics(projectId) {
    return (params) => fetchEpics({ ...params, projectId: resolveProjectId(projectId) })
}

function useDebouncedCallback(cb, delay = 400) {
    const t = useRef()
    return useCallback(
        (...args) => {
            if (t.current) window.clearTimeout(t.current)
            t.current = window.setTimeout(() => cb(...args), delay)
        },
        [cb, delay]
    )
}

export function useAsyncSelect(fetcher, pageSize = 20) {
    const [open, setOpen] = useState(false)
    const [options, setOptions] = useState([])
    const [loading, setLoading] = useState(false)
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)

    const fetcherRef = useRef(fetcher)
    fetcherRef.current = fetcher

    const load = useCallback(
        async (nextPage, nextSearch, replace) => {
            setLoading(true)
            try {
                const { items, hasMore: more } = await fetcherRef.current({
                    search: nextSearch,
                    page: nextPage,
                    pageSize,
                })
                setOptions((prev) => (replace ? items : [...prev, ...items]))
                setHasMore(more)
                setPage(nextPage)
            } catch (e) {
                message.error('Failed to load options.')
            } finally {
                setLoading(false)
            }
        },
        [pageSize]
    )

    useEffect(() => {
        setOptions([])
        setHasMore(true)
        setPage(1)
        setSearch('')
    }, [fetcher])

    const onDropdownVisibleChange = useCallback(
        (v) => {
            setOpen(v)
            if (v && options.length === 0 && !loading) {
                load(1, search, true)
            }
        },
        [load, options.length, loading, search]
    )

    const debouncedSearch = useDebouncedCallback((q) => {
        const cleaned = q.trim()
        setSearch(cleaned)
        setHasMore(true)
        load(1, cleaned, true)
    }, 400)

    const onPopupScroll = useCallback(
        (e) => {
            const el = e.currentTarget
            const nearBottom =
                el.scrollTop + el.clientHeight >= el.scrollHeight - 10
            if (nearBottom && hasMore && !loading) {
                load(page + 1, search, false)
            }
        },
        [hasMore, loading, load, page, search]
    )

    const notFoundContent = useMemo(
        () => (loading ? <Spin size="small" /> : null),
        [loading]
    )

    return {
        open,
        options,
        loading,
        notFoundContent,
        onDropdownVisibleChange,
        onPopupScroll,
        onSearch: debouncedSearch,
        reset: () => {
            setOptions([])
            setHasMore(true)
            setPage(1)
        },
    }
}

export async function fetchEpics({ search, page, pageSize, projectId }) {
    const resolvedProjectId = resolveProjectId(projectId)
    if (!resolvedProjectId) {
        return { items: [], hasMore: false }
    }
    const payload = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
        projectId: resolvedProjectId,
        ...(search ? { title: search } : {}),
        filter: {
            ...(search ? { title: search } : {}),
        },
    }
    const params = new URLSearchParams(payload).toString()
    const res = await getEpicsListForTC(params)
    const data = Array.isArray(res?.data) ? res.data : res?.data?.data ?? []
    return {
        items: data.map((e) => ({ value: e._id, label: e.title })),
        hasMore: data.length === pageSize,
    }
}

export async function fetchSprints({ search, page, pageSize, projectId }) {
    const resolvedProjectId = resolveProjectId(projectId)
    if (!resolvedProjectId) {
        return { items: [], hasMore: false }
    }
    const payload = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
        ...(search ? { search } : {}),
        filter: {
            populate: {
                name: 'projectId',
                val: 'name',
            },
            projectId: resolvedProjectId,
        },
    }
    const res = await getSprintListForTC(payload)
    const list = res?.data?.data ?? res?.data ?? []
    const isNext = res?.data?.is_next
    return {
        items: list.map((s) => ({ value: s._id, label: s.title })),
        hasMore:
            typeof isNext === 'boolean' ? isNext : list.length === pageSize,
    }
}

export async function fetchTags({ search, page, pageSize }) {
    const params = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
        ...(search ? { search } : {}),
    }
    const res = await apiGetTagList(params)
    const list = res?.data?.data ?? res?.data ?? []
    const isNext = res?.data?.isNext
    return {
        items: list.map((t) => ({ value: t._id, label: t.name })),
        hasMore:
            typeof isNext === 'boolean' ? isNext : list.length === pageSize,
    }
}
